import type { MediaRenderer } from 'voximplant-websdk/Media/MediaRenderer';

import { getVideoContainerId } from 'store/conference/helpers/getVideoContainerId';

import { playVideo } from 'utils/playVideo';

class VideoManager {
    private _map = new Map<
        string,
        {
            /** Вывод видео в пиктограмме пользователя */
            draw: () => void;
            /** Вывод видео в центре приложения */
            drawMain: () => void;
            /** Удалить видео из пиктограммы пользователя */
            remove: () => void;
            /** Удалить видео в центре приложения */
            removeMain: () => void;
        }
    >();

    /** Добавить пользователя в список управления вывода видео с камеры */
    public set({ mediaRenderer, userId }: { mediaRenderer: MediaRenderer; userId: string }) {
        function getCameraNode() {
            return document.getElementById(getVideoContainerId({ id: userId, location: 'user' }));
        }

        function getMainContentNode() {
            return document.getElementById(getVideoContainerId({ id: userId, location: 'center' }));
        }

        function clearNode(node: HTMLElement | null) {
            if (node) node.textContent = '';
        }

        function remove() {
            clearNode(getCameraNode());
        }

        function removeMain() {
            clearNode(getMainContentNode());
        }

        // проверка на существование видео от пользователя
        function isVideoAlreadyExist(containerNode: HTMLElement) {
            const video = containerNode.getElementsByTagName('video')[0];

            if (!video) return false;
            // тэг video может существовать, но по какой-то причине у него нет нужных атрибутов для отрисовки
            const [hasEmptyId, hasEmptySrc] = ['id', 'src'].map(attr => !video.getAttribute(attr));

            return hasEmptyId && hasEmptySrc;
        }

        this._map.set(userId, {
            draw: () => {
                const node = getCameraNode();

                if (node) {
                    setTimeout(() => {
                        if (isVideoAlreadyExist(node)) remove();
                        mediaRenderer.render(node);
                        playVideo(node);
                    }, 100);
                }
            },
            drawMain: () => {
                const node = getMainContentNode();

                if (node) {
                    setTimeout(() => {
                        if (isVideoAlreadyExist(node)) removeMain();
                        mediaRenderer.render(node);
                        playVideo(node);
                    }, 100);
                }
            },
            remove,
            removeMain,
        });

        return this.getVideoManagerByUserId(userId);
    }

    /** Удалить пользователя из списка управления вывода видео с камеры */
    public remove(userId: string) {
        this._map.delete(userId);

        return this._map;
    }

    /** Получить пользователя из списка управления вывода видео с камеры */
    public getVideoManagerByUserId(userId: string) {
        return this._map.get(userId);
    }
}

/** Экземпляр управления видео с камеры пользователей */
export const videoManager = new VideoManager();
